import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useBranding } from "../../context/BrandingContext";
import { Store, Copy, Check, Webhook } from "lucide-react";

export default function AdminSettings() {
  const [form, setForm] = useState({ razorpay_key_id: "", razorpay_key_secret: "", platform_fee_percent: 10 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Webhook URL is derived server-side for THIS tenant — never built in the
  // browser, so it always matches the endpoint Razorpay should actually call.
  const [webhook, setWebhook] = useState({ url: "", events: [], slug: "" });
  const [copied, setCopied] = useState(false);
  const { branding } = useBranding();
  const isSingleVendor = branding?.business_mode === "single_vendor";

  useEffect(() => {
    let alive = true;
    api.get("/admin/settings")
      .then(({ data }) => {
        if (!alive) return;
        setForm((f) => ({ ...f, razorpay_key_id: data.razorpay_key_id || "", platform_fee_percent: data.platform_fee_percent ?? 10 }));
        setWebhook({
          url: data.razorpay_webhook_url || "",
          events: data.razorpay_webhook_events || [],
          slug: data.tenant_slug || "",
        });
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const copyWebhook = async () => {
    if (!webhook.url) return;
    try {
      await navigator.clipboard.writeText(webhook.url);
    } catch {
      // Clipboard API needs a secure context; fall back to a temp selection
      // so this still works over plain http on a LAN admin panel.
      const ta = document.createElement("textarea");
      ta.value = webhook.url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
    setCopied(true);
    toast.success("Webhook URL copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    const fee = Number(form.platform_fee_percent);
    if (!Number.isFinite(fee) || fee < 0 || fee > 100) return toast.error("Platform fee must be between 0 and 100");
    setSaving(true);
    try {
      await api.put("/admin/settings", { ...form, platform_fee_percent: fee });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-settings-page">
      <div className="overline mb-2">Configuration</div>
      <h1 className="font-display text-4xl mb-8">Platform Settings</h1>

      <div
        className="bg-white border border-[hsl(var(--kb-border))] p-6 mb-6 flex items-start gap-4"
        data-testid="storefront-mode-card"
      >
        <div className="w-10 h-10 rounded-sm bg-[hsl(var(--kb-surface-alt))] flex items-center justify-center shrink-0">
          <Store size={18} className="text-[hsl(var(--kb-primary))]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline mb-1">Storefront mode</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-base font-medium"
              data-testid="storefront-mode-value"
            >
              {isSingleVendor ? "Single vendor" : "Multi vendor"}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                isSingleVendor
                  ? "bg-amber-50 text-amber-700"
                  : "bg-green-50 text-green-700"
              }`}
            >
              {isSingleVendor ? "Solo" : "Marketplace"}
            </span>
          </div>
          <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
            {isSingleVendor
              ? "You are the sole seller on this storefront. The Sellers section is hidden — all products you create belong to your own catalog."
              : "Sellers can self-register on your storefront and submit products for your moderation. You can also add sellers manually from the Sellers section."}
          </p>
          <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-2">
            This mode is set by the platform team. Contact support to switch.
          </p>
        </div>
      </div>

      <div
        className="bg-white border border-[hsl(var(--kb-border))] p-6 mb-6"
        data-testid="razorpay-webhook-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-sm bg-[hsl(var(--kb-surface-alt))] flex items-center justify-center shrink-0">
            <Webhook size={18} className="text-[hsl(var(--kb-primary))]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1">Razorpay Webhook URL</div>
            <p className="text-xs text-[hsl(var(--kb-muted))] mb-3">
              Copy this into <strong>Razorpay Dashboard → Settings → Webhooks →
              Add New Webhook</strong>. It is unique to
              {webhook.slug ? <> your store (<code>{webhook.slug}</code>)</> : " your store"},
              so Razorpay deliveries are matched to the right storefront.
            </p>

            {webhook.url ? (
              <div className="flex items-stretch gap-2 flex-wrap sm:flex-nowrap">
                <code
                  className="flex-1 min-w-0 block text-xs bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] px-3 py-2 rounded-sm break-all"
                  data-testid="razorpay-webhook-url"
                >
                  {webhook.url}
                </code>
                <button
                  type="button"
                  onClick={copyWebhook}
                  className="kb-btn-outline !py-2 !px-4 inline-flex items-center gap-2 text-sm shrink-0 whitespace-nowrap"
                  data-testid="copy-webhook-url-btn"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied" : "Copy Webhook URL"}
                </button>
              </div>
            ) : (
              <div className="text-xs text-[hsl(var(--kb-muted))]" data-testid="razorpay-webhook-empty">
                {loading ? "Loading…" : "Webhook URL unavailable — reload the page."}
              </div>
            )}

            {webhook.events?.length > 0 && (
              <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-3">
                Subscribe to these events:{" "}
                {webhook.events.map((e) => (
                  <code key={e} className="mr-1">{e}</code>
                ))}
              </p>
            )}
            <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-2">
              After saving the webhook in Razorpay, paste the webhook&apos;s secret into
              <strong> Branding → Razorpay → Webhook Secret</strong> so deliveries can be verified.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-5">
        <div>
          <label className="overline block mb-1">Platform Razorpay Key ID</label>
          <input className="kb-input" value={form.razorpay_key_id} onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })} data-testid="adm-rzp-id" />
          <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">Used to collect platform fees from sellers.</p>
          <p className="text-xs text-amber-700 mt-1" data-testid="adm-rzp-checkout-note">
            Not used for customer checkout. To accept online payments from your customers, add your Razorpay
            keys under <strong>Branding → Razorpay</strong>.
          </p>
        </div>
        <div>
          <label className="overline block mb-1">Platform Razorpay Key Secret</label>
          <input type="password" className="kb-input" value={form.razorpay_key_secret} onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })} data-testid="adm-rzp-secret" />
        </div>
        <div>
          <label className="overline block mb-1">Platform Fee (%)</label>
          <input type="number" className="kb-input" value={form.platform_fee_percent} onChange={(e) => setForm({ ...form, platform_fee_percent: Number(e.target.value) })} data-testid="adm-fee" />
        </div>
        <button className="kb-btn-primary" onClick={save} disabled={loading || saving} data-testid="adm-save">{saving ? "Saving…" : "Save Settings"}</button>
      </div>
    </div>
  );
}
